import React from "react";

/**
 * Minimal error boundary so a single unhandled JS error doesn't leave the
 * user staring at a blank white page (which is what happened on production
 * after the booking submit when the toast tried to render a FastAPI
 * validation error array as a React child).
 *
 * We log the error to the console so it's visible in DevTools, and show a
 * friendly recovery card with a "Reload" button.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = this.state.error?.message || "Si è verificato un errore imprevisto.";
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full bg-white border border-[#E8E2D9] rounded-2xl shadow-sm p-7 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#AC5D45]/10 flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="font-display text-2xl text-[#1E3A5F] mb-2">Qualcosa è andato storto</h1>
          <p className="text-sm text-[#635F59] mb-1">
            Riprova ricaricando la pagina. Se il problema persiste, contattaci via WhatsApp.
          </p>
          <p className="text-[11px] text-[#9C9890] font-mono mt-3 mb-5 break-words" data-testid="error-message">
            {msg}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="error-reload-btn"
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-7 py-2.5 text-sm font-semibold transition"
          >
            Ricarica la pagina
          </button>
        </div>
      </div>
    );
  }
}
